// Quote engine loader. A page places the engine with placeholder elements
// and this script:
//
//   <div data-quote-engine="bridging_loan" data-part="estimate"></div>
//   <div data-quote-engine="bridging_loan" data-part="flow"></div>
//   <script src="/engine/engine.js" defer></script>
//
// data-part: "estimate" (the quick-estimate card), "flow" (refine sections,
// results, adviser band) or "all" (both, the default). A page needs the flow
// somewhere; without an estimate placeholder the card goes at the top of it.
// Optional: data-mode="admin" (real lender names, behind the admin login),
// data-preset='{"charge":"second_charge"}', data-sticky-top="88" (the page's
// sticky header height), data-flat-card (no drop shadow on the card).
//
// Each part draws in its own shadow root, so the page's CSS and the
// engine's never meet. The brand (colours, fonts, phone, links) comes from
// the site's address via GET /api/engine/:financeType.
import { linkPreset } from './linkPreset';
import { startBridgingEngine } from './bridging/controller';
import { estimateHtml, flowHtml } from './bridging/markup';
import { startDevelopmentEngine } from './development/controller';
import { devEstimateHtml, devFlowHtml } from './development/markup';
import { adminLoginHtml, BRIDGING_DIALOG, DEV_FINANCE_DIALOG, dialogHtml } from './sharedMarkup';
import { Parts } from './dom';
import css from './engine.css';
import { ensureFonts, themeCss } from './theme';
import type { DevFinanceForm, EngineConfig, MountOptions } from './types';
import { attachTooltips } from './widgets/tooltip';

const scriptSrc = (document.currentScript as HTMLScriptElement | null)?.src || '/engine/engine.js';
const ENGINE_BASE = scriptSrc.replace(/[^/]*$/, '');
const FINANCE_TYPE_ALIASES: Record<string, string> = {
  bridging: 'bridging_loan', bridging_loan: 'bridging_loan',
  development: 'development_finance', development_finance: 'development_finance',
};

function readOptions(mounts: HTMLElement[]): MountOptions {
  const attr = (name: string) => mounts.map((m) => m.getAttribute(name)).find((v) => v !== null) ?? null;
  let preset: Record<string, unknown> | null = null;
  const presetAttr = attr('data-preset');
  if (presetAttr) {
    try { preset = JSON.parse(presetAttr); } catch { console.warn('quote engine: data-preset is not valid JSON'); }
  }
  // Landing pages set this global (src/landingPages.ts).
  const globalPreset = (window as unknown as { BRIDGING_EYE_PRESET?: Record<string, unknown> }).BRIDGING_EYE_PRESET;
  preset = preset || (globalPreset && typeof globalPreset === 'object' ? globalPreset : null);
  // A link can start the comparison regulated or commercial, on figures typed elsewhere, and with a
  // product chosen (from the regulated and commercial bridging pages: src/linkPreset.ts).
  const link = linkPreset(window.location.search);
  if (Object.keys(link.preset).length) preset = { ...(preset || {}), ...link.preset };
  return {
    mode: attr('data-mode') === 'admin' ? 'admin' : 'public',
    preset,
    applyRef: link.applyRef,
    stickyTop: Number(attr('data-sticky-top')) || 0,
    stickyHeader: attr('data-sticky-header'),
    flatCard: attr('data-flat-card') !== null,
  };
}

function appendHtml(root: ShadowRoot, html: string): void {
  const template = document.createElement('template');
  template.innerHTML = html;
  root.appendChild(template.content);
}

function shadowFor(mount: HTMLElement, config: EngineConfig, stickyTop: number): ShadowRoot {
  const root = mount.shadowRoot || mount.attachShadow({ mode: 'open' });
  root.innerHTML = `<style>${css}\n${themeCss(config.brand.theme)}\n:host { --qe-sticky-top: ${stickyTop}px; }</style>`;
  return root;
}

async function adminSignedIn(root: ShadowRoot): Promise<void> {
  if ((await fetch('/admin/me')).ok) return;
  const holder = document.createElement('div');
  holder.innerHTML = adminLoginHtml();
  root.appendChild(holder);
  await new Promise<void>((resolve) => {
    const formEl = root.getElementById('admin-login-form') as HTMLFormElement;
    const errEl = root.getElementById('admin-login-error') as HTMLElement;
    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      errEl.classList.add('hidden');
      try {
        const res = await fetch('/admin/login', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            email: (root.getElementById('admin-email') as HTMLInputElement).value,
            password: (root.getElementById('admin-password') as HTMLInputElement).value,
          }),
        });
        if (!res.ok) throw new Error(res.status === 429 ? 'Too many attempts - please try again in a few minutes.' : 'Incorrect email or password.');
        holder.remove();
        resolve();
      } catch (err) {
        errEl.textContent = (err as Error).message;
        errEl.classList.remove('hidden');
      }
    });
  });
}

/** What each finance type draws and runs. */
interface FinanceEngine {
  estimateHtml(config: EngineConfig<never>, flatCard: boolean): string;
  flowHtml(config: EngineConfig<never>): string;
  dialog: typeof BRIDGING_DIALOG;
  start(parts: Parts, config: EngineConfig<never>, options: MountOptions): void;
}
const ENGINES: Record<string, FinanceEngine> = {
  bridging_loan: {
    estimateHtml: (config, flat) => estimateHtml(config as EngineConfig, flat),
    flowHtml: (config) => flowHtml(config as EngineConfig),
    dialog: BRIDGING_DIALOG,
    start: (parts, config, options) => startBridgingEngine(parts, config as EngineConfig, options),
  },
  development_finance: {
    estimateHtml: (_config, flat) => devEstimateHtml(flat),
    flowHtml: (config) => devFlowHtml(config as EngineConfig<DevFinanceForm>),
    dialog: DEV_FINANCE_DIALOG,
    start: (parts, config, options) => startDevelopmentEngine(parts, config as EngineConfig<DevFinanceForm>, options),
  },
};

async function mountEngine(financeType: string, mounts: HTMLElement[]): Promise<void> {
  const engine = ENGINES[financeType];
  const res = await fetch(`/api/engine/${financeType}`);
  if (!res.ok) throw new Error(`quote engine: no ${financeType} engine on this site`);
  const config = await res.json() as EngineConfig<never>;
  const options = readOptions(mounts);
  ensureFonts(config.brand.theme, ENGINE_BASE);

  const partOf = (m: HTMLElement) => m.getAttribute('data-part') || 'all';
  const estimateMount = mounts.find((m) => partOf(m) === 'estimate');
  const flowMount = mounts.find((m) => partOf(m) === 'flow' || partOf(m) === 'all') || mounts[0];
  const parts = new Parts();

  const flowRoot = shadowFor(flowMount, config, options.stickyTop);
  if (options.mode === 'admin') {
    const banner = document.createElement('div');
    banner.className = 'admin-banner';
    banner.textContent = 'Internal preview - real lender names - not visible to customers';
    flowRoot.appendChild(banner);
    await adminSignedIn(flowRoot);
  }

  if (estimateMount && estimateMount !== flowMount) {
    const estimateRoot = shadowFor(estimateMount, config, options.stickyTop);
    appendHtml(estimateRoot, engine.estimateHtml(config, options.flatCard));
    attachTooltips(estimateRoot);
    parts.add(estimateRoot);
  } else {
    appendHtml(flowRoot, `<div class="wrap" style="padding:32px 0">${engine.estimateHtml(config, options.flatCard)}</div>`);
  }
  appendHtml(flowRoot, engine.flowHtml(config) + dialogHtml(config.brand, config.form as EngineConfig['form'], engine.dialog));
  attachTooltips(flowRoot);
  parts.add(flowRoot);

  // A page header that changes height (e.g. 88px on desktop, 64px on a
  // phone): measure it now and on resize, for scrolling and sticky bars.
  if (options.stickyHeader) {
    const header = document.querySelector(options.stickyHeader);
    const measure = () => {
      options.stickyTop = header ? Math.round(header.getBoundingClientRect().height) : options.stickyTop;
      parts.roots.forEach((root) => (root.host as HTMLElement).style.setProperty('--qe-sticky-top', `${options.stickyTop}px`));
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
  }

  engine.start(parts, config, options);
}

function boot(): void {
  const byType = new Map<string, HTMLElement[]>();
  document.querySelectorAll<HTMLElement>('[data-quote-engine]').forEach((el) => {
    const type = FINANCE_TYPE_ALIASES[el.dataset.quoteEngine || ''];
    if (!type) {
      console.warn(`quote engine: unknown finance type "${el.dataset.quoteEngine}"`);
      return;
    }
    byType.set(type, [...(byType.get(type) || []), el]);
  });
  byType.forEach((mounts, type) => {
    mountEngine(type, mounts).catch((err) => console.error(err));
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
